import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LoginPage, MainApp } from './components';
import { authService } from './apiServices';
import { initializeStatusBar, forceStatusBarStyling, handleSafeAreaInsets } from './utils/statusBarUtils';
import { db } from './services/sync';
import './App.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <span>Завантаження...</span>
      </div>
    );
  }

  return currentUser ? <>{children}</> : <Navigate to="/login" replace />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <span>Завантаження...</span>
      </div>
    );
  }

  return currentUser ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        
        <Route 
          path="/dashboard/*" 
          element={
            <ProtectedRoute>
              <MainApp />
            </ProtectedRoute>
          } 
        />
        
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

function App(): React.ReactElement {
  useEffect(() => {
    authService.initializeAuth();
    
    // Initialize IndexedDB для синхронізації
    db.open().catch(err => {
      console.error('Failed to open IndexedDB:', err);
    });
    
    // Initialize status bar styling for iOS
    initializeStatusBar();
    forceStatusBarStyling();
    handleSafeAreaInsets();
    
    // iOS PWA touch target fix - fix click/touch offset issue
    const isIOSPWA = window.matchMedia('(display-mode: standalone)').matches && 
                     /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOSPWA) {
      // Fix touch target offset issue in iOS PWA
      const fixTouchTargets = () => {
        // Remove any transform that might cause offset
        const app = document.querySelector('.App') as HTMLElement;
        if (app) {
          app.style.transform = 'none';
          app.style.webkitTransform = 'none';
        }
        
        // Ensure body and html don't have transforms
        document.body.style.transform = 'none';
        document.body.style.webkitTransform = 'none';
        document.documentElement.style.transform = 'none';
        document.documentElement.style.webkitTransform = 'none';
        
        // Force repaint to ensure touch targets align
        void app?.offsetHeight;
      };
      
      // Fix viewport and inputs
      const fixIOSInputs = () => {
        // Ensure all inputs have proper touch-action
        const inputs = document.querySelectorAll('input, textarea, select, button, a');
        inputs.forEach((input) => {
          const htmlInput = input as HTMLElement;
          htmlInput.style.touchAction = 'manipulation';
          htmlInput.style.webkitUserSelect = 'text';
          htmlInput.style.userSelect = input.tagName === 'INPUT' || input.tagName === 'TEXTAREA' ? 'text' : 'none';
          // Remove any transforms
          htmlInput.style.transform = 'none';
          htmlInput.style.webkitTransform = 'none';
        });
      };
      
      // Run fixes
      fixTouchTargets();
      fixIOSInputs();
      
      // Re-run after a delay to catch dynamically added elements
      setTimeout(() => {
        fixTouchTargets();
        fixIOSInputs();
      }, 100);
      
      setTimeout(() => {
        fixTouchTargets();
        fixIOSInputs();
      }, 500);
      
      // Fix on first touch/interaction
      const handleFirstTouch = () => {
        fixTouchTargets();
        fixIOSInputs();
      };
      
      document.addEventListener('touchstart', handleFirstTouch, { once: true, passive: true });
      document.addEventListener('click', handleFirstTouch, { once: true });
      
      // Fix on orientation change
      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          fixTouchTargets();
          fixIOSInputs();
        }, 100);
      });
      
      // Fix touch event coordinates by ensuring proper viewport
      const fixTouchEvents = () => {
        // Remove any CSS transforms that might offset touch coordinates
        const style = document.createElement('style');
        style.textContent = `
          @media (display-mode: standalone) {
            html, body, #root, .App {
              transform: none !important;
              -webkit-transform: none !important;
              transform-origin: 0 0 !important;
              -webkit-transform-origin: 0 0 !important;
            }
          }
        `;
        document.head.appendChild(style);
      };
      
      fixTouchEvents();
    }
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <NotificationProvider>
          <div className="App">
            <AppRoutes />
          </div>
        </NotificationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;


